import SlideOutPanel from "./slideOutPanel.js";
import htmlToElement from "../../js/htmlToElement";
import {
  getBodyWrapper, getBodyScroll, getBodyElement, getSlideOutElement,
} from "./slideOutUtil";

export default class SlideOutManager {
  constructor() {
    this.panels = [];
    this.activePanel = null;
    this.focusableElementsString = "a[href], "
        + "area[href], "
        + "input:not([disabled]), "
        + "select:not([disabled]),"
        + "textarea:not([disabled]),"
        + "button:not([disabled]),"
        + "iframe,"
        + "object,"
        + "embed,"
        + "*[tabindex],"
        + "*[contenteditable]";
    this.footerTopRedirector = null;
    this.footerBottomRedirector = null;

    this.initPanels();

    // Set up elements and event handlers to fix the page tab order
    //
    // Per EDU-7038, the tab order should go from the main content -> the slide-out panel -> the footer,
    // but because the panels are all after the footer in the DOM that doesn't happen by default.
    // The panels can't be moved because this structure is needed to let the main page scroll independently of the panel
    //
    // We add some dummy elements at the top and bottom of the footer that detect when focus is about to
    // enter or leave the footer and redirect focus to the panel as appropriate. They're hidden by default so they don't
    // capture focus when the panel is hidden.
    const footer = document.querySelector(".govuk-footer");

    if (footer) {
      const elementBeforeFooter = footer.previousElementSibling;
      this.footerTopRedirector = htmlToElement(`<div id="fds-footer-top-redirector" tabindex="0" hidden="hidden"></div>`);
      this.footerBottomRedirector = htmlToElement(`<div id="fds-footer-bottom-redirector" tabindex="0" hidden="hidden"></div>`);

      footer.prepend(this.footerTopRedirector);
      footer.append(this.footerBottomRedirector);

      this.footerTopRedirector.addEventListener("focus", (e) => {
        if (!this.activePanel) {
          return;
        }

        // The element that had focus before the user tabbed to the top of the footer
        const prevFocusElement = e.relatedTarget;

        if (elementBeforeFooter.contains(prevFocusElement)) {
          // They're tabbing forwards into the footer
          // Redirect focus to the first element in the panel
          this.activePanel.focusFirstElement();
        } else if (footer.contains(prevFocusElement)) {
          // They're tabbing backwards and were about to go from the footer to the main content
          // Redirect focus to the last element in the panel
          this.activePanel.focusLastElement();
        }
      });

      this.footerBottomRedirector.addEventListener("focus", (e) => {
        if (!this.activePanel) {
          return;
        }

        // The element that had focus before the user tabbed to the bottom of the footer
        const prevFocusElement = e.relatedTarget;

        if (footer.contains(prevFocusElement)) {
          // They're tabbing forwards through the footer and have hit the end
          // Redirect focus into the browser chrome
          setTimeout(() => {
            this.activePanel.focusBottomRedirector();
            document.activeElement.blur();
          }, 0);
        } else if (this.activePanel.panelSelector.contains(prevFocusElement)) {
          // They're tabbing backwards and they've just come out of the panel into the footer
          // Redirect focus to the last focusable element in the main content
          const focusableInMain = elementBeforeFooter.querySelectorAll(this.focusableElementsString);

          if (focusableInMain.length > 0) {
            focusableInMain[focusableInMain.length - 1].focus();
          }
        }
      });
    }

    // Load panel specified in fragment identifier, if it exists
    const searchParams = new URLSearchParams(window.location.search);

    // eslint-disable-next-line no-restricted-syntax
    for (const [panelId, panelActive] of searchParams.entries()) {
      if (panelActive && this.panelExists(panelId)) {
        getBodyScroll().style.transition = "none";
        this.managePanel(panelId);
      }
    }

    // Set up event handlers to make slide out panel triggers show their respective panels
    document.querySelectorAll("*[data-open-slide-out-panel]").forEach((element) => {
      element.addEventListener("click", (event) => {
        const targetPanelId = event.target.getAttribute("data-open-slide-out-panel");
        this.managePanel(targetPanelId);
        event.stopPropagation();
        event.preventDefault();
      });
    });

    document.querySelectorAll("*[data-close-slide-out-panel]").forEach((element) => {
      element.addEventListener("click", (event) => {
        this.hideActivePanel();
        event.stopPropagation();
        event.preventDefault();
      });
    });
  }

  initPanels() {
    // Move the whole contents of the body into a container,
    // so we can get the panels and content to scroll independently
    if (getBodyWrapper() == null) {
      const detachedBody = document.querySelectorAll("body>*");
      const bodyScrollWrapperDivs = htmlToElement(
        `<div id="body-scroll" class="fds-body-scroll"><div id="body-wrapper"></div></div>`,
      );

      detachedBody.forEach((element) => {
        bodyScrollWrapperDivs.querySelector("#body-wrapper").appendChild(element);
      });
      getBodyElement().appendChild(bodyScrollWrapperDivs);
    }

    getSlideOutElement().forEach((panelDiv) => {
      this.createPanel(panelDiv.getAttribute("id"));
      getBodyElement().appendChild(panelDiv);
    });
  }

  createPanel(panelId) {
    this.panels[panelId] = new SlideOutPanel(panelId);
  }

  managePanel(panelId) {
    // Check panel exists
    if (!this.panelExists(panelId)) return;

    // Check it's not the current panel
    if (this.panels[panelId] === this.activePanel) return;

    if (this.activePanel) {
      // If there's an active panel, hide it.
      // Pass showPanel() in a callback function that's run when the other panel has finished hiding
      this.activePanel.hidePanel(() => this.panels[panelId].showPanel());
    } else {
      // If there's no active panel just show this one straight away
      this.panels[panelId].showPanel();
    }

    this.activePanel = this.panels[panelId];
    this.footerTopRedirector.hidden = false;
    this.footerBottomRedirector.hidden = false;
  }

  hideActivePanel() {
    this.activePanel.hidePanel();
    this.activePanel = null;
    this.footerTopRedirector.hidden = true;
    this.footerBottomRedirector.hidden = true;
  }

  panelExists(panelId) {
    return Object.prototype.hasOwnProperty.call(this.panels, panelId);
  }
}
